/**
 * navigationStateMachine.ts — Pure decision-chain for player navigation (F1b).
 *
 * `decideTransition` applies a fixed 4-tier priority chain:
 *   1. Nav Rule via decide() from logic-engine F1a  (first-match-wins)
 *   2. clicked single_choice option.nextScreenId    (D6: multiple_choice skipped)
 *   3. button_next.nextScreenId
 *   4. index+1 → COMPLETE at boundary
 *
 * Pure module: no React, no DOM. Unit-testable via table.
 * F1b — reuses F1a decide() directly, does NOT re-implement eval logic.
 */

// WEB-2015: прямой модуль вместо бочки `@/logic-engine` — бочка тянет схемы
// правил (zod) в первый экран покупателя, а переходам нужен только decide().
import { decide } from '@/logic-engine/decide';
import { OPTION_NAV_BLOCK_TYPES } from '@/logic-engine/funnel-graph';
import type { Logic, EvalContext } from '@/logic-engine';
import type { EditorScreen } from '@/store/editorSlice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClickedOption {
  /**
   * Block type — tier 2 applies to single-pick types only: 'single_choice'
   * and 'image_choice' (the player passes the latter only for a single pick,
   * with or without autoAdvance — owner decision 22.10.2026). multiple_choice is
   * never a tier-2 source (D6 RATIFIED).
   */
  blockType: string;
  /** Per-option nextScreenId from the option the user selected. */
  nextScreenId?: string;
}

export type TransitionResult =
  | { type: 'GOTO_SCREEN'; targetId: string }
  | { type: 'RESTART' }
  | { type: 'GOTO_ENDING'; targetId: string }
  /**
   * WEB-2164: `ruleId` — id правила, породившего уход. По нему плеер собирает
   * адрес СЕРВЕРНОЙ точки ухода, которая достаёт адрес назначения сама (в
   * запросе адреса нет и не будет). Поле необязательное: без него уход идёт
   * запасным путём — прямым переходом по `url`.
   */
  | { type: 'REDIRECT_URL'; url: string; ruleId?: string }
  /**
   * WEB-3819: правило «перейти к пейволлу воронки» — закончить квиз сейчас.
   * Плеер квиза исполняет его ровно как COMPLETE (`fireQuizComplete`).
   */
  | { type: 'PAYWALL' }
  | { type: 'COMPLETE' };

export interface DecideTransitionInput {
  screens: EditorScreen[];
  /** Navigation rules (Rule[]). Empty array = no rules → tiers 2-4 only. */
  logic: Logic;
  currentScreen: EditorScreen;
  currentIndex: number;
  /** Populated from user selection. Tier 2 only acts on single_choice. */
  clickedOption?: ClickedOption;
  context: EvalContext;
  /**
   * WEB-3819: исполнять ли правило «перейти к пейволлу воронки». `true`
   * передаёт только плеер квиза. Нет флага (плеер пейволла: «с пейволла
   * обратно в квиз нельзя», решение владельца 23.10) — действие
   * пропускается дальше по лестнице, как правила видимости.
   */
  allowPaywallJump?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Block types whose clicked option may steer tier 2 — the ONE set of the
 * shared funnel-graph module (single_choice + image_choice; owner decision
 * 22.10.2026 extended D6 to image_choice). The wizard passes image_choice
 * only for a single pick (blockHonorsOptionNav).
 */
const TIER2_SINGLE_PICK_TYPES: ReadonlySet<string> = OPTION_NAV_BLOCK_TYPES;

/**
 * normScreenName — сравнение имён экранов без регистра и лишних пробелов.
 * Зеркало нормализации из logic-engine/resolveScreenRefs.ts (WEB-780), чтобы
 * имя, введённое автором, разрешалось здесь ровно так же, как там.
 */
function normScreenName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * resolveEndingScreen — find the ending screen for GOTO_ENDING actions.
 *
 * Priority: explicit target (по коду экрана → по ИМЕНИ экрана) → first screen
 * with result_screen block → last screen.
 * Exported for independent testing and cycle-guard fallback.
 *
 * WEB-1717: цель у действия «перейти к результату» — свободный текст (в
 * редакторе правил у неё нет списка экранов), а приведение «имя → код» сделано
 * только для GOTO_SCREEN (resolveScreenRefs.ts). Раньше сюда приходило имя и
 * возвращалось как есть — плеер такой «код» не находил и уносил покупателя на
 * последний экран. Теперь имя разрешается, а неразрешимая цель НЕ возвращается
 * наружу выдуманным кодом: падаем на экран результата / последний экран.
 */
export function resolveEndingScreen(
  screens: EditorScreen[],
  target?: string,
): string {
  if (target) {
    const byId = screens.find((s) => s.id === target);
    if (byId) return byId.id;

    const wanted = normScreenName(target);
    if (wanted.length > 0) {
      const byName = screens.find(
        (s) =>
          typeof s.name === 'string' &&
          s.name.trim().length > 0 &&
          normScreenName(s.name) === wanted,
      );
      if (byName) return byName.id;
    }
    // Неразрешимая цель → общие запаски ниже (не возвращаем несуществующий код).
  }
  const resultScreen = screens.find((s) =>
    s.blocks.some((b) => b.type === 'result_screen'),
  );
  if (resultScreen) return resultScreen.id;
  return screens[screens.length - 1]?.id ?? '';
}

/**
 * linearFallback — tier 4: index+1, or COMPLETE if beyond the last screen.
 */
function linearFallback(
  screens: EditorScreen[],
  currentIndex: number,
): TransitionResult {
  if (currentIndex < screens.length - 1) {
    return { type: 'GOTO_SCREEN', targetId: screens[currentIndex + 1].id };
  }
  return { type: 'COMPLETE' };
}

// ---------------------------------------------------------------------------
// decideTransition — main entry point
// ---------------------------------------------------------------------------

/**
 * decideTransition — apply the 4-tier decision chain for the next screen.
 *
 * Decision order (first match wins):
 *   1. Nav Rule from logic-engine decide() — GOTO_SCREEN/RESTART/GOTO_ENDING/REDIRECT_URL
 *   2. clickedOption.nextScreenId (single pick: single_choice, auto-advancing image_choice; multiple_choice skipped per D6)
 *   3. button_next.nextScreenId on current screen
 *   4. index+1 linear fallback → COMPLETE at boundary
 *
 * Fail-safe: broken GOTO_SCREEN target (screen not found) → linearFallback (not throw).
 * SHOW/HIDE are visibility actions, not nav — tier 1 passes them through to tier 2+.
 */
export function decideTransition(input: DecideTransitionInput): TransitionResult {
  const { screens, logic, currentScreen, currentIndex, clickedOption, context } = input;

  // ── Tier 1: Nav Rule via decide() ────────────────────────────────────────
  const { nav } = decide(logic, context, currentScreen.id);
  if (nav !== null) {
    const { action } = nav;

    if (action.type === 'RESTART') {
      return { type: 'RESTART' };
    }

    if (action.type === 'GOTO_ENDING') {
      return {
        type: 'GOTO_ENDING',
        targetId: resolveEndingScreen(screens, action.target),
      };
    }

    // Флаг — boolean: сравнение «=== true» не пишем ради веса первого экрана.
    if (action.type === 'GOTO_PAYWALL' && input.allowPaywallJump) {
      return { type: 'PAYWALL' };
    }

    if (action.type === 'REDIRECT_URL') {
      // WEB-2164: id правила — единственное, чем плеер может назвать цель
      // серверной точке ухода; дальше по коду он уже неизвестен.
      return { type: 'REDIRECT_URL', url: action.target, ruleId: nav.ruleId };
    }

    if (action.type === 'GOTO_SCREEN') {
      const idx = screens.findIndex((s) => s.id === action.target);
      if (idx >= 0) {
        return { type: 'GOTO_SCREEN', targetId: action.target };
      }
      // Fail-safe: broken target → skip to linear fallback directly (not tier 2/3)
      console.warn(
        `[F1b] GOTO_SCREEN: target "${action.target}" not found in screens, falling back to index+1`,
      );
      return linearFallback(screens, currentIndex);
    }

    // SHOW/HIDE = visibility actions, NOT nav → fall through to tiers 2-4.
    // GOTO_PAYWALL без allowPaywallJump (плеер пейволла) — так же, WEB-3819.
  }

  // ── Tier 2: single-pick per-option target (D6: multiple_choice SKIPPED) ──
  if (
    clickedOption &&
    TIER2_SINGLE_PICK_TYPES.has(clickedOption.blockType) &&
    clickedOption.nextScreenId
  ) {
    const idx = screens.findIndex((s) => s.id === clickedOption.nextScreenId);
    if (idx >= 0) {
      return { type: 'GOTO_SCREEN', targetId: clickedOption.nextScreenId };
    }
    // Fail-safe: broken option target → fall through to tier 3/4
  }

  // ── Tier 3: button_next.nextScreenId ──────────────────────────────────────
  const buttonNext = currentScreen.blocks.find((b) => b.type === 'button_next');
  if (buttonNext?.nextScreenId) {
    const idx = screens.findIndex((s) => s.id === buttonNext.nextScreenId);
    if (idx >= 0) {
      return { type: 'GOTO_SCREEN', targetId: buttonNext.nextScreenId };
    }
    // Fail-safe: broken nextScreenId → fall through to tier 4
  }

  // ── Tier 4: linear index+1 / COMPLETE (backward compat) ──────────────────
  return linearFallback(screens, currentIndex);
}
